import moment from "moment";
import classNames from "classnames";
import { MediaQuery } from "@outplayed/responsive";
import { HeaderPatch } from "@ugg/shared/components/common/HeaderPatch";
import YellowAlert from "@ugg/shared/components/common/YellowAlert";
import { getQueueTypeName } from "@ugg/shared/utils/queue-type-helpers";
import { numberWithCommas } from "@ugg/shared/utils/math";
import { useTierList } from "@ugg/shared/api/requests/tier-lists/tier-list";
import { convertPatchDisplay } from "@ugg/shared/utils/patch-helpers";

import { useUGGPatches } from "@ugg/shared/api/requests/patches";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { useSeoPages, usePageTitleAndDesc } from "@ugg/shared/utils/seo-helpers";

import { TIER_LIST_PAGES, SPECIAL_TIER_LIST_PAGES, useTierListPage } from "@ugg/shared/pages/tier-list-pages";

function getTierListTitle(page: TIER_LIST_PAGES | SPECIAL_TIER_LIST_PAGES, params: { role: string; rank: string; duo: string }) {
  const { role, rank, duo } = params;

  const tierListTitles: { [key: string]: { main: string; secondary?: string } } = {
    [TIER_LIST_PAGES.DEFAULT_TIER_LIST]: { main: "LoL Tier List", secondary: `for All Roles, ${rank}` },
    [TIER_LIST_PAGES.TOP_TIER_LIST]: { main: "LoL Tier List", secondary: `for Top, ${rank}` },
    [TIER_LIST_PAGES.JUNGLE_TIER_LIST]: { main: "LoL Tier List", secondary: `for Jungle, ${rank}` },
    [TIER_LIST_PAGES.MID_TIER_LIST]: { main: "LoL Tier List", secondary: `for Mid, ${rank}` },
    [TIER_LIST_PAGES.ADC_TIER_LIST]: { main: "LoL Tier List", secondary: `for ADC, ${rank}` },
    [TIER_LIST_PAGES.SUPPORT_TIER_LIST]: { main: "LoL Tier List", secondary: `for Support, ${rank}` },
    [TIER_LIST_PAGES.DUO_TIER_LIST]: { main: `LoL Duo Tier List`, secondary: `for ${duo}, ${rank}` },
    [TIER_LIST_PAGES.COMBAT_TIER_LIST]: { main: `Combat Statistics`, secondary: `for ${role}, ${rank}` },
    [TIER_LIST_PAGES.OBJECTIVES_TIER_LIST]: { main: `Objectives Statistics`, secondary: `for ${role}, ${rank}` },
    [SPECIAL_TIER_LIST_PAGES.PICK_URF_TIER_LIST]: { main: "URF Tier List" },
    [SPECIAL_TIER_LIST_PAGES.ARURF_TIER_LIST]: { main: "ARURF Tier List" },
    [SPECIAL_TIER_LIST_PAGES.ARAM_TIER_LIST]: { main: "ARAM Tier List" },
    [SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST]: { main: "ARAM Mayhem Tier List" },
    [SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_AUGMENT_TIER_LIST]: { main: "ARAM Mayhem Augment Tier List" },
    [SPECIAL_TIER_LIST_PAGES.NEXUS_BLITZ_TIER_LIST]: { main: "Nexus Blitz Tier List" },
    [SPECIAL_TIER_LIST_PAGES.ONE_FOR_ALL_TIER_LIST]: { main: "One for All Tier List" },
    [SPECIAL_TIER_LIST_PAGES.ARENA_TIER_LIST]: { main: "Arena Tier List" },
    [SPECIAL_TIER_LIST_PAGES.ARENA_DUO_TIER_LIST]: { main: "Arena Duos" },
  };

  return tierListTitles[page];
}

interface TierListHeaderProps {
  className?: string;
  totalMatches: number | undefined | null;
  lastUpdated: string | undefined | null;
  loading?: boolean;
  includeMeta?: boolean;
  patchNotesCTA?: React.ReactNode;
}

export function TierListHeader(props: TierListHeaderProps) {
  const { className, totalMatches, lastUpdated, loading, includeMeta, patchNotesCTA } = props;
  const page = useTierListPage();
  const currentQueryParams = useQueryString();

  const seoPages = useSeoPages();
  const validateQueryParams = useValidateQueryParams(validParams);
  const getPageTitleAndDesc = usePageTitleAndDesc();
  const { data: uggPatches } = useUGGPatches();

  const isSpecialGameMode = !!Object.values(SPECIAL_TIER_LIST_PAGES).find((specialPage) => page === specialPage);
  const { data: tierList, loading: loadingTierList } = useTierList(page, {
    params: currentQueryParams,
    ssr: true,
    skip: isSpecialGameMode,
  });

  const latestUGGPatch = uggPatches?.[0] || "";
  const params = validateQueryParams(TIER_LIST_PAGES.DEFAULT_TIER_LIST, currentQueryParams, true);
  const baseData = tierList && latestUGGPatch === params.patch ? tierList : null;

  const completeParams = validateQueryParams(page, currentQueryParams, true);
  const validPageParams: Record<string, any> = validParams[page];
  const queueTypeName = getQueueTypeName(completeParams.queueType);

  const rankOptions: Array<{ value: string; label: string }> = validPageParams.rank || [];
  const roleOptions: Array<{ value: string; label: string }> = validPageParams.role || [];
  const duoOptions: Array<{ value: string; label: string }> = validPageParams.duo || [];
  const currentRank = rankOptions.find((rank) => rank.value === completeParams.rank);
  const currentRole = roleOptions.find((role) => role.value === completeParams.role);
  const currentDuo = duoOptions.find((duo) => duo.value === completeParams.duo);

  const title = getTierListTitle(page, {
    rank: currentRank?.label || "",
    role: currentRole?.label || "",
    duo: currentDuo?.label || "",
  });

  if (!title && (!queueTypeName || !lastUpdated || !totalMatches)) {
    return null;
  }

  const showWarning = baseData && baseData.total_matches < 100000;
  const displayPatch = completeParams.patch ? convertPatchDisplay(completeParams.patch).replace("_", ".") : undefined;
  const visible: React.CSSProperties =
    !loading || (queueTypeName && lastUpdated && totalMatches) ? { visibility: "visible" } : { visibility: "hidden" };

  const seoMeta = seoPages[page] || {};
  const metaDescription = (seoMeta.description || "").replace("{patch}", displayPatch || "");

  return (
    <div className={classNames(className)}>
      {getPageTitleAndDesc(page, { patch: completeParams.patch })}
      <h1 className="flex items-center gap-[12px] text-[36px] text-white leading-[34px] font-['Barlow']">
        {title && (
          <>
            <div className="contents max-sm:grid max-sm:gap-[20px]">
              <span>
                <span className="text-white font-semibold max-sm:text-[24px]">{title.main}</span>{" "}
                {title.secondary && (
                  <span className="text-accent-gray-100 font-normal max-sm:text-[20px]">{title.secondary}</span>
                )}
              </span>
              <div className="contents max-sm:flex max-sm:gap-[12px]">
                {displayPatch && <HeaderPatch patch={displayPatch} className="relative top-[2px]" />}
                {patchNotesCTA}
              </div>
            </div>
          </>
        )}
      </h1>
      <div className="flex items-center flex-wrap mt-[18px] max-sm:mt-[24px] gap-[18px]" style={visible}>
        <div className="flex px-[12px] py-[8px] rounded-[3px] bg-purple-100 text-[14px] whitespace-nowrap [&_span]:text-accent-gray-100 max-xs:flex-col max-xs:gap-[4px]">
          <div className="whitespace-pre">
            <span className="!text-white">
              <strong>{queueTypeName}</strong>
              <span className="!text-lavender-300"> / </span>
            </span>
            <span>{`Last Updated: `}</span>
            <strong>{lastUpdated ? moment(lastUpdated).fromNow() : "-"}</strong>
            <span className="!text-lavender-300"> / </span>
          </div>
          <div className="flex whitespace-pre">
            <span>{`Champions Analyzed: `}</span>{" "}
            <strong>{totalMatches ? numberWithCommas(Math.round(totalMatches) * 10) : "-"}</strong>{" "}
            {showWarning && <YellowAlert className="ml-[8px]" label="Low sample size" width={20} />}
          </div>
        </div>
        {includeMeta && (
          <div className="flex items-center flex-1 text-[12px] h-[35px] font-medium text-accent-gray-100 max-sm:flex-[0_0_100%] max-sm:h-auto">
            {metaDescription}
          </div>
        )}
      </div>
    </div>
  );
}
